import { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  createPublicCheckoutSession,
  fetchPublicQuote,
  PublicQuoteResponse,
} from '@/lib/lendliftedService';
import { logFirebaseEvent } from '@/lib/tracking';

export default function CustomerCheckoutPage() {
  const TIP_PERCENT_OPTIONS = [15, 20, 25] as const;
  type TipMode = 'none' | 'preset' | 'custom';
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const checkoutStatus = searchParams.get('checkout');
  const [isLoadingQuote, setIsLoadingQuote] = useState(true);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [quote, setQuote] = useState<PublicQuoteResponse | null>(null);
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tipMode, setTipMode] = useState<TipMode>('none');
  const [selectedTipPercent, setSelectedTipPercent] = useState<number>(0);
  const [customTipAmountInput, setCustomTipAmountInput] = useState('');
  const [customTipError, setCustomTipError] = useState<string | null>(null);

  useEffect(() => {
    if (checkoutStatus === 'success') {
      toast.success('Payment complete. Thank you!');
    }
    if (checkoutStatus === 'cancelled') {
      toast.message('Checkout was cancelled. You can try again anytime.');
    }
  }, [checkoutStatus]);

  useEffect(() => {
    if (!token) {
      setError('This financing link is invalid.');
      setIsLoadingQuote(false);
      return;
    }

    let cancelled = false;
    const loadQuote = async () => {
      setIsLoadingQuote(true);
      setError(null);
      try {
        const data = await fetchPublicQuote(token);
        if (!cancelled) {
          setQuote(data);
          setSelectedTierId(data.tiers && data.tiers.length > 0 ? data.tiers[0].id : null);
          setTipMode('none');
          setSelectedTipPercent(0);
          setCustomTipAmountInput('');
          setCustomTipError(null);
        }
      } catch (loadError: any) {
        if (!cancelled) {
          setError(loadError?.message || 'Could not load this financing link.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingQuote(false);
        }
      }
    };

    loadQuote();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const startCheckout = async () => {
    if (!token || !quote || quote.status !== 'open') {
      return;
    }

    const baseAmountCents = Math.round(quote.amount * 100);
    const rawCustomTipInput = customTipAmountInput.trim();
    const parsedCustomTipAmount = Number(rawCustomTipInput || '0');
    if (quote.tippingEnabled && tipMode === 'custom') {
      const hasInput = rawCustomTipInput.length > 0;
      const invalidNumber = hasInput && !Number.isFinite(parsedCustomTipAmount);
      if (invalidNumber || parsedCustomTipAmount < 0) {
        setCustomTipError('Please enter a valid tip amount.');
        toast.error('Invalid tip amount.');
        return;
      }
    }
    const customTipAmountCents =
      Number.isFinite(parsedCustomTipAmount) && parsedCustomTipAmount > 0
        ? Math.round(parsedCustomTipAmount * 100)
        : 0;
    const tipAmountCents = !quote.tippingEnabled
      ? 0
      : tipMode === 'custom'
        ? customTipAmountCents
        : Math.round(baseAmountCents * (selectedTipPercent / 100));

    const maxPercentBps = quote.tipPolicy?.maxPercentBps ?? 5000;
    const maxAbsoluteCents = quote.tipPolicy?.maxAbsoluteCents ?? 50000;
    const calculatedPercentTipCents = Math.floor((baseAmountCents * maxPercentBps) / 10000);
    const maxAllowedTipCents = baseAmountCents > 0
      ? Math.min(calculatedPercentTipCents, maxAbsoluteCents)
      : maxAbsoluteCents;
    if (quote.tippingEnabled && tipMode === 'custom' && customTipAmountCents > maxAllowedTipCents) {
      setCustomTipError(`Custom gratuity exceeds the current limit (${formatCurrency(maxAllowedTipCents / 100, quote.currency)} max).`);
      toast.error('Please lower the custom gratuity amount.');
      return;
    }

    if (quote.tippingEnabled && tipMode === 'custom' && customTipAmountCents > 0) {
      const impliedPct = baseAmountCents > 0 ? Number(((customTipAmountCents / baseAmountCents) * 100).toFixed(2)) : null;
      trackTipSelection({ percentage: impliedPct, amountCents: customTipAmountCents });
    }

    setIsStartingCheckout(true);
    try {
      const session = await createPublicCheckoutSession(token, undefined, tipAmountCents, selectedTierId || undefined);
      window.location.assign(session.checkoutUrl);
    } catch (checkoutError: any) {
      console.error('Failed to start checkout', checkoutError);
      toast.error(checkoutError?.message || 'Could not start checkout. Please try again.');
      setIsStartingCheckout(false);
    }
  };

  if (isLoadingQuote) {
    return (
      <div className="grid min-h-screen place-content-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-700" />
      </div>
    );
  }

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);

  const selectedTier = quote?.tiers?.find((tier) => tier.id === selectedTierId) || quote?.tiers?.[0] || null;
  const isDepositOnly = quote?.checkoutMode === 'deposit_only';
  const baseAmount = selectedTier
    ? Math.max(0, selectedTier.amount - (isDepositOnly ? 0 : Number(quote?.depositAmount || 0)))
    : (quote?.amount || 0);
  const totalProjectAmount = Number(quote?.totalAmount || quote?.amount || 0);
  const depositAmount = Number(quote?.depositAmount || 0);
  const remainingBalanceAmount = Number(quote?.balanceRemainingAmount || 0);
  const chargeTodayBaseAmount = Number(quote?.chargeTodayAmount || quote?.amount || 0);
  const baseAmountCents = Math.round(baseAmount * 100);
  const parsedCustomTipAmount = Number(customTipAmountInput || '0');
  const customTipAmountCents =
    Number.isFinite(parsedCustomTipAmount) && parsedCustomTipAmount > 0
      ? Math.round(parsedCustomTipAmount * 100)
      : 0;
  const tipAmountCents = quote && quote.tippingEnabled
    ? (tipMode === 'custom'
        ? customTipAmountCents
        : Math.round(baseAmountCents * (selectedTipPercent / 100)))
    : 0;
  const tipAmount = tipAmountCents / 100;
  const totalAmount = baseAmount + tipAmount;
  const monthlyEstimate = totalAmount / 12;
  const payIn4Estimate = totalAmount / 4;
  const maxPercentBps = quote?.tipPolicy?.maxPercentBps ?? 5000;
  const maxAbsoluteCents = quote?.tipPolicy?.maxAbsoluteCents ?? 50000;
  const calculatedPercentTipCents = Math.floor((baseAmountCents * maxPercentBps) / 10000);
  const maxAllowedTipCents = baseAmountCents > 0
    ? Math.min(calculatedPercentTipCents, maxAbsoluteCents)
    : maxAbsoluteCents;
  const maxAllowedTipAmount = maxAllowedTipCents / 100;

  const trackTipSelection = (next: { percentage: number | null; amountCents: number }) => {
    if (!quote) return;
    logFirebaseEvent('quote_tip_selected', {
      quote_id: quote.quoteId,
      percentage: next.percentage ?? 0,
      amount: Number((next.amountCents / 100).toFixed(2)),
      currency: quote.currency.toUpperCase(),
    });
  };

  const selectNoTip = () => {
    setTipMode('none');
    setSelectedTipPercent(0);
    setCustomTipError(null);
    trackTipSelection({ percentage: 0, amountCents: 0 });
  };

  const selectPresetTip = (pct: number) => {
    setTipMode('preset');
    setSelectedTipPercent(pct);
    setCustomTipError(null);
    trackTipSelection({ percentage: pct, amountCents: Math.round(baseAmountCents * (pct / 100)) });
  };

  const selectTier = (tierId: string) => {
    setSelectedTierId(tierId);
    if (!quote) return;
    logFirebaseEvent('tier_selected', {
      quote_id: quote.quoteId,
      tier_id: tierId,
    });
  };

  const selectCustomTipMode = () => {
    setTipMode('custom');
    setSelectedTipPercent(0);
    setCustomTipError(null);
  };

  const trackCustomTipIfValid = () => {
    if (!quote || tipMode !== 'custom') return;
    if (customTipAmountCents <= 0) return;
    if (customTipAmountCents > maxAllowedTipCents) {
      setCustomTipError(`Custom gratuity exceeds the current limit (${formatCurrency(maxAllowedTipAmount, quote.currency)} max).`);
      return;
    }
    setCustomTipError(null);
    const impliedPct = baseAmountCents > 0 ? Number(((customTipAmountCents / baseAmountCents) * 100).toFixed(2)) : null;
    trackTipSelection({ percentage: impliedPct, amountCents: customTipAmountCents });
  };

  return (
    <section className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
      <div className="rounded-3xl border border-slate-900/10 bg-white/95 p-8 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Financing Checkout</p>
        <h1 className="mt-2 font-[Sora] text-3xl text-slate-900">Split this purchase over time</h1>

        {error ? (
          <p className="mt-4 text-sm text-red-700">{error}</p>
        ) : quote ? (
          <>
            {checkoutStatus === 'success' && (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <p className="font-semibold">Payment confirmed</p>
                <p className="mt-1">Need to share this confirmation with a partner or decision-maker?</p>
                <button
                  type="button"
                  className="mt-2 text-emerald-900 underline"
                  onClick={async () => {
                    const cleanUrl = `${window.location.origin}${window.location.pathname}`;
                    await navigator.clipboard.writeText(cleanUrl);
                    toast.success('Confirmation link copied.');
                  }}
                >
                  Copy confirmation link
                </button>
              </div>
            )}
            <p className="mt-3 text-slate-700">Offered by {quote.businessName}</p>
            {quote.description && <p className="mt-2 text-sm text-slate-600">{quote.description}</p>}

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              {quote.tiers && quote.tiers.length > 1 && quote.status === 'open' && (
                <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-sm font-medium text-slate-900">Choose an option</p>
                  <div className="mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 md:grid md:grid-cols-3 md:overflow-visible md:pb-0">
                    {quote.tiers.map((tier, index) => {
                      const active = selectedTierId === tier.id;
                      const tierNetAmount = Math.max(0, tier.amount - (isDepositOnly ? 0 : Number(quote.depositAmount || 0)));
                      const isRecommended = quote.tiers && quote.tiers.length === 3 && index === 1;
                      const tierRankLabel =
                        quote.tiers && quote.tiers.length >= 3
                          ? (index === 0 ? 'Good' : index === 1 ? 'Better' : 'Best')
                          : (index === 0 ? 'Option A' : 'Option B');
                      return (
                        <button
                          key={tier.id}
                          type="button"
                          onClick={() => selectTier(tier.id)}
                          className={`min-w-[240px] snap-start rounded-xl border p-3 text-left transition md:min-w-0 ${
                            active
                              ? 'border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/15'
                              : isRecommended
                                ? 'border-amber-200 bg-amber-50/60 text-slate-900'
                                : 'border-slate-200 bg-white text-slate-900'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                              active ? 'bg-white/15 text-white' : isRecommended ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-700'
                            }`}>
                              {tierRankLabel}
                            </span>
                            {isRecommended ? (
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${active ? 'bg-white/15 text-white' : 'bg-slate-900 text-white'}`}>
                                Recommended
                              </span>
                            ) : <span />}
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-medium">{tier.name}</span>
                            <span className="font-semibold">{formatCurrency(tierNetAmount, quote.currency)}</span>
                          </div>
                          {Number(quote.depositAmount || 0) > 0 && !isDepositOnly && (
                            <p className={`mt-1 text-xs ${active ? 'text-slate-200' : 'text-slate-500'}`}>
                              {formatCurrency(tier.amount, quote.currency)} total − {formatCurrency(Number(quote.depositAmount || 0), quote.currency)} deposit
                            </p>
                          )}
                          {tier.description && (
                            <p className={`mt-1 text-xs ${active ? 'text-slate-200' : 'text-slate-600'}`}>{tier.description}</p>
                          )}
                          <div className={`mt-2 flex flex-wrap gap-1 text-[11px] ${active ? 'text-slate-200' : 'text-slate-600'}`}>
                            <span className={`rounded-full px-2 py-0.5 ${active ? 'bg-white/10' : 'bg-slate-100'}`}>Secure checkout</span>
                            <span className={`rounded-full px-2 py-0.5 ${active ? 'bg-white/10' : 'bg-slate-100'}`}>Pay-over-time options</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-sm text-slate-600">{isDepositOnly ? 'Amount due today' : 'Purchase amount'}</p>
              <p className="mt-1 text-3xl font-semibold text-slate-900">
                {formatCurrency(baseAmount, quote.currency)}
              </p>
              {isDepositOnly && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="font-semibold">Deposit-only checkout</p>
                  <p className="mt-1">
                    You are paying a deposit of {formatCurrency(depositAmount || chargeTodayBaseAmount, quote.currency)} today to secure your booking.
                  </p>
                  <p className="mt-1">
                    Remaining balance: {formatCurrency(remainingBalanceAmount, quote.currency)} on a total project amount of {formatCurrency(totalProjectAmount, quote.currency)}.
                  </p>
                </div>
              )}
              {quote.tippingEnabled && quote.status === 'open' && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-sm font-medium text-slate-900">Add a Gratuity</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={`rounded-full border px-3 py-1.5 text-sm ${
                        tipMode === 'none'
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 bg-white text-slate-700'
                      }`}
                      onClick={selectNoTip}
                    >
                      None
                    </button>
                    {TIP_PERCENT_OPTIONS.map((pct) => (
                      <button
                        key={pct}
                        type="button"
                        className={`rounded-full border px-3 py-1.5 text-sm ${
                          tipMode === 'preset' && selectedTipPercent === pct
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-slate-300 bg-white text-slate-700'
                        }`}
                        onClick={() => selectPresetTip(pct)}
                      >
                        {pct}% ({formatCurrency((baseAmount * pct) / 100, quote.currency)})
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`rounded-full border px-3 py-1.5 text-sm ${
                        tipMode === 'custom'
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 bg-white text-slate-700'
                      }`}
                      onClick={selectCustomTipMode}
                    >
                      Custom Amount
                    </button>
                  </div>
                  {tipMode === 'custom' && (
                    <div className="mt-3 grid gap-2 sm:max-w-xs">
                      <label htmlFor="customTipAmount" className="text-xs font-medium text-slate-700">
                        Custom gratuity amount
                      </label>
                      <Input
                        id="customTipAmount"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={customTipAmountInput}
                        onChange={(event) => {
                          setCustomTipAmountInput(event.target.value);
                          if (customTipError) setCustomTipError(null);
                        }}
                        onBlur={trackCustomTipIfValid}
                      />
                      <p className="text-xs text-slate-500">
                        Enter a dollar amount. Max allowed for this quote: {formatCurrency(maxAllowedTipAmount, quote.currency)}.
                      </p>
                      {customTipError && <p className="text-xs text-red-600">{customTipError}</p>}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 space-y-1 text-sm text-slate-700">
                <div className="flex items-center justify-between">
                  <span>Service amount</span>
                  <span>{formatCurrency(baseAmount, quote.currency)}</span>
                </div>
                {quote.tippingEnabled && (
                  <div className="flex items-center justify-between">
                    <span>Gratuity</span>
                    <span>{formatCurrency(tipAmount, quote.currency)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900">
                  <span>{isDepositOnly ? 'Total due today' : 'Total to finance'}</span>
                  <span>{formatCurrency(totalAmount, quote.currency)}</span>
                </div>
              </div>
              {isDepositOnly ? (
                <p className="mt-3 text-sm text-slate-700">
                  The remaining balance is <strong>{formatCurrency(remainingBalanceAmount, quote.currency)}</strong>. Your seller may send a separate financing link later.
                </p>
              ) : quote.tiers && quote.tiers.length > 1 && Number(quote.depositAmount || 0) > 0 ? (
                <p className="mt-3 text-sm text-slate-700">
                  Deposit already applied: <strong>{formatCurrency(Number(quote.depositAmount || 0), quote.currency)}</strong>. The amount above reflects the selected option balance.
                </p>
              ) : (
                <p className="mt-3 text-sm text-slate-700">
                  As low as <strong>${monthlyEstimate.toFixed(2)}/mo</strong> or{' '}
                  <strong>${payIn4Estimate.toFixed(2)}</strong> in 4 payments.
                </p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                Financing options such as Klarna, Afterpay/Clearpay, or Affirm appear at checkout when eligible.
              </p>
            </div>

            {quote.status === 'paid' && (
              <p className="mt-5 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                This quote has already been paid.
              </p>
            )}

            {quote.status === 'expired' && (
              <p className="mt-5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
                This financing link has expired. Ask the seller to send a new one.
              </p>
            )}

            <Button
              onClick={startCheckout}
              disabled={quote.status !== 'open' || isStartingCheckout}
              className="mt-6"
            >
              {isStartingCheckout ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Continue to secure checkout
            </Button>
          </>
        ) : null}
      </div>
    </section>
  );
}
